import api from '@/lib/axiosConfig';
import rollbarClient from '@/lib/rollbar/rollbarClient';

import type { FetchOptions } from './types';
import type Rollbar from 'rollbar';

/** Core fetch helper to avoid repeating axios logic */
async function _fetchData<TData, TParams>(
  url: string,
  options: FetchOptions<TParams>,
  prefix = ''
): Promise<TData | undefined> {
  const { params, signal, headers } = options;
  const fullUrl = `${prefix}${url}`;

  try {
    const response = await api.get<TData>(fullUrl, {
      withCredentials: true,
      params,
      signal,
      headers, // forward cookie on SSR
    });
    return response.data;
  } catch (error) {
    if (signal?.aborted) {
      console.warn(`Request aborted: ${fullUrl}`);
      return;
    }
    rollbarClient.error('Error fetching data', error as Rollbar.LogArgument, { url: fullUrl });
    throw error; // allow callers (SSR + auth) to distinguish failures
  }
}

/** Fetch data from any endpoint */
export function fetchData<TData = unknown, TParams = Record<string, unknown>>(
  url: string,
  options: FetchOptions<TParams> = {},
  withRestAPIPrefix = false
) {
  if (withRestAPIPrefix) {
    return _fetchData<TData, TParams>(url, options, '/rest_api/');
  }

  return _fetchData<TData, TParams>(url, options);
}

/** Fetch data from the REST API endpoint */
export function fetchDataRestApi<TData = unknown, TParams = Record<string, string | number | boolean>>(
  url: string,
  options: FetchOptions<TParams> = {}
) {
  return _fetchData<TData, TParams>(url, options, '/rest_api/');
}
